import React, { useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useFeatureEntitlements } from "@frontegg/react";
import { Box, Button, Card, Divider, Tab, Tabs } from "@mui/material";
import { PageHeader, ScrollArea } from "@outpave/ds";
import { HugeiconsIcon } from "@hugeicons/react";
import { Edit04Icon } from "@hugeicons-pro/core-stroke-rounded";
import Role from "enums/Role";
import StatusType from "enums/StatusType";
import { AccountDetailsDrawerType } from "enums/AccountDetailsDrawerType";
import { useCheckUserRole } from "hooks/useCheckUserRole";
import useAccountData from "hooks/useAccountData";
import { useDrawerRecord } from "hooks/useDrawerRecord";
import IdNotFoundPage from "components/common/IdNotFoundPage";
import { RenderAccountStatus } from "components/common/RenderAccountStatus";
import { Inline, Stack } from "components/layout/reusableLayouts";
import { SettingsCard } from "components/platform/accounts/SettingsCard";
import AccountHolderDetails from "components/platform/accounts/AccountHolderDetails";
import CardProgramsSettings from "components/platform/accounts/CardProgramsSettings";
import CompanyRewardsSettings from "components/platform/accounts/CompanyRewardsSettings";
import CompanyPlansPanel from "components/platform/accounts/CompanyPlansPanel";
import { DeactivateAccountModal } from "components/platform/accounts/DeactivateAccountModal";
import EditAccountDrawer from "components/platform/accounts/EditAccountDrawer";
import PlatformUsersTable from "components/platform/users/PlatfromUsersTable";
import PlatformAddNewUserDrawer from "components/platform/users/PlatformAddNewUserDrawer";
import PlatformUserProfileDrawer from "components/platform/users/PlatformUserProfileDrawer";
import { CompanySwitcher } from "components/platform/companyConsole/CompanySwitcher";
import { CompanyConsole } from "components/platform/companyConsole/CompanyConsole";
import type { FilterData } from "pages/platform/PlatformUsers";
import { ACCOUNTS_PATH } from "utils/platformAccounts/accountPaths";

export enum AccountHolderTab {
  Dashboard = "dashboard",
  Accounts = "accounts",
  Cards = "cards",
  Transactions = "transactions",
  Ledger = "ledger",
  Activity = "activity",
  Settings = "settings",
  Details = "details",
  Users = "users",
  Plans = "plans",
  CardPrograms = "cardPrograms",
  Rewards = "rewards",
}

const tabId = (tab: AccountHolderTab) => `account-holder-tab-${tab}`;
const panelId = (tab: AccountHolderTab) => `account-holder-panel-${tab}`;

/** One account holder: its details, its users, its rewards and, for a platform owner, its plans and card programs. */
const AccountHolderContent: React.FC<{ accountId: string }> = ({
  accountId,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isPlatformOwner = useCheckUserRole(Role.PlatformOwner);
  const isPlatformAdminOrHigher = useCheckUserRole(Role.PlatformAdmin);
  const isAdminOrHigher = useCheckUserRole(Role.Admin);
  const { isEntitled: showLedger } = useFeatureEntitlements(
    "feature_transaction_journal",
  );

  const editDrawer = useDrawerRecord<true>();
  // A cancelled edit resets the form after the drawer has slid out, not while it is still on screen.
  const discardEdits = useRef(false);
  const closeEdit = (refresh?: boolean) => {
    discardEdits.current = !refresh;
    editDrawer.close();
  };
  const [deactivating, setDeactivating] = useState(false);
  const [addingUser, setAddingUser] = useState(false);
  const [profile, setProfile] = useState<{
    userId: string;
    edit: boolean;
    reactivate: boolean;
  } | null>(null);
  const [refreshUsers, setRefreshUsers] = useState(0);

  const {
    formikAddAccount: formik,
    company,
    refetchCompany,
    isLoading,
    isSavingAccount,
  } = useAccountData({ accountId, handleClose: () => closeEdit(true) });

  const tabs = [
    { id: AccountHolderTab.Dashboard, label: t("Dashboard") },
    { id: AccountHolderTab.Accounts, label: t("Accounts") },
    { id: AccountHolderTab.Cards, label: t("Cards") },
    { id: AccountHolderTab.Transactions, label: t("Transactions") },
    ...(showLedger
      ? [{ id: AccountHolderTab.Ledger, label: t("Ledger") }]
      : []),
    { id: AccountHolderTab.Activity, label: t("Activity") },
    { id: AccountHolderTab.Users, label: t("Users") },
    { id: AccountHolderTab.Settings, label: t("Settings") },
  ];
  const settingsTabs = [
    { id: AccountHolderTab.Details, label: t("Business information") },
    ...(isPlatformOwner
      ? [
          { id: AccountHolderTab.Plans, label: t("Plans") },
          { id: AccountHolderTab.CardPrograms, label: t("Card programs") },
        ]
      : []),
    ...(isPlatformAdminOrHigher
      ? [{ id: AccountHolderTab.Rewards, label: t("Rewards") }]
      : []),
  ];
  const requestedTab = searchParams.get("tab");
  const legacySettings = ["details", "plans", "cardPrograms"].includes(
    requestedTab || "",
  );
  const activeTab = legacySettings
    ? AccountHolderTab.Settings
    : (tabs.find((tab) => tab.id === requestedTab)?.id ??
      AccountHolderTab.Dashboard);
  const settingsTab =
    settingsTabs.find(
      (tab) =>
        tab.id ===
        (legacySettings ? requestedTab : searchParams.get("section")),
    )?.id ?? AccountHolderTab.Details;
  const changeTab = (id: AccountHolderTab) =>
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      next.set("tab", id);
      // Resource drawers and the ledger's account belong to the section that opened them. Date and account filters persist.
      [
        "transaction",
        "card",
        "account",
        "request",
        "entry",
        "ledgerAccount",
        "eventDetail",
        "status",
        "type",
        "search",
        "metric",
      ].forEach((key) => next.delete(key));
      return next;
    });

  const filterData = useMemo<FilterData>(
    () => ({ companyId: accountId, status: [], searchTerm: "" }),
    [accountId],
  );

  if (!company || company.id !== accountId) {
    return (
      <IdNotFoundPage title={t("Account")} isLoading={isLoading} showSkeleton />
    );
  }

  const name = (company.businessDBA || company.businessLegalName) as string;
  const backToAccounts = () => navigate(ACCOUNTS_PATH);
  const afterEditClose = () => {
    if (discardEdits.current) formik.resetForm();
    editDrawer.afterClose();
  };

  return (
    // The same insets as the table pages, so the content edges match the Accounts list.
    <Stack
      gap="var(--space-5)"
      sx={{
        px: { xs: "var(--space-4)", md: "var(--space-7)" },
        pt: "var(--space-4)",
        pb: "var(--space-7)",
        height: "100%",
        minHeight: 0,
      }}
    >
      <PageHeader
        breadcrumbs={[
          { label: t("Accounts"), onClick: backToAccounts },
          { label: name },
        ]}
        back={{ label: t("Back to accounts"), onClick: backToAccounts }}
        title={name}
        meta={
          <RenderAccountStatus
            accountStatus={company.status}
            isDeactivateRequested={company.isDeactivateRequested}
          />
        }
        subtitle={company.businessLegalName}
        actions={<CompanySwitcher companyId={accountId} name={name} />}
      />
      <Tabs
        aria-label={t("Account sections")}
        value={activeTab}
        onChange={(_event, id: AccountHolderTab) => changeTab(id)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ flexShrink: 0 }}
      >
        {tabs.map((tab) => (
          <Tab
            key={tab.id}
            value={tab.id}
            label={tab.label}
            id={tabId(tab.id)}
            // Only the active panel is mounted, so only its tab points at one.
            aria-controls={tab.id === activeTab ? panelId(tab.id) : undefined}
          />
        ))}
      </Tabs>

      <Box
        role="tabpanel"
        id={panelId(activeTab)}
        aria-labelledby={tabId(activeTab)}
        sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
      >
        {activeTab === AccountHolderTab.Users ? (
          <Card
            variant="outlined"
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            {isAdminOrHigher && (
              <>
                <Inline justify="end" sx={{ p: "var(--space-4)" }}>
                  <Button onClick={() => setAddingUser(true)}>
                    {t("Add User")}
                  </Button>
                </Inline>
                <Divider />
              </>
            )}
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <PlatformUsersTable
                filterData={filterData}
                handleOpenUserProfileDrawer={(
                  userId,
                  edit = false,
                  reactivate = false,
                ) => setProfile({ userId, edit, reactivate })}
                refreshUsers={refreshUsers}
                handleRefreshUsers={() => setRefreshUsers((count) => count + 1)}
                showAccount={false}
              />
            </Box>
          </Card>
        ) : activeTab === AccountHolderTab.Settings ? (
          <Stack gap="var(--space-5)" sx={{ flex: 1, minHeight: 0 }}>
            <Tabs
              aria-label={t("Account settings")}
              value={settingsTab}
              onChange={(_event, id: string) =>
                setSearchParams((previous) => {
                  const next = new URLSearchParams(previous);
                  next.set("tab", "settings");
                  next.set("section", id);
                  return next;
                })
              }
            >
              {settingsTabs.map((tab) => (
                <Tab key={tab.id} value={tab.id} label={tab.label} />
              ))}
            </Tabs>
            <ScrollArea sx={{ flex: 1, minHeight: 0 }}>
              <Stack gap="var(--space-5)" sx={{ minHeight: "100%" }}>
                {settingsTab === AccountHolderTab.Details && (
                  <>
                    <SettingsCard
                      title={t("Business information")}
                      action={
                        <Button
                          variant="default-outlined"
                          size="small"
                          startIcon={
                            <HugeiconsIcon icon={Edit04Icon} size={16} />
                          }
                          onClick={() => editDrawer.show(true)}
                        >
                          {t("Edit")}
                        </Button>
                      }
                    >
                      <AccountHolderDetails account={company} />
                    </SettingsCard>
                    {company.status === StatusType.ACTIVE &&
                      !company.isDeactivateRequested && (
                        <Inline justify="end">
                          <Button
                            variant="danger-outlined"
                            onClick={() => setDeactivating(true)}
                          >
                            {t("platform.deactivateAccount")}
                          </Button>
                        </Inline>
                      )}
                  </>
                )}
                {settingsTab === AccountHolderTab.Plans && (
                  <CompanyPlansPanel companyId={accountId} />
                )}
                {settingsTab === AccountHolderTab.CardPrograms && (
                  <CardProgramsSettings companyId={accountId} />
                )}
                {settingsTab === AccountHolderTab.Rewards && (
                  <CompanyRewardsSettings companyId={accountId} />
                )}
              </Stack>
            </ScrollArea>
          </Stack>
        ) : (
          <CompanyConsole
            key={`${accountId}:${activeTab}`}
            companyId={accountId}
            defaultCurrency={company.companyCurrency || "USD"}
            tab={activeTab}
            showLedger={showLedger}
          />
        )}
      </Box>

      {editDrawer.record && (
        <EditAccountDrawer
          open={editDrawer.open}
          onAfterClose={afterEditClose}
          handleClose={closeEdit}
          drawerType={AccountDetailsDrawerType.EditExistingAccount}
          formik={formik}
          companyId={accountId}
          savingAccount={isSavingAccount}
        />
      )}
      <DeactivateAccountModal
        accountId={accountId}
        accountName={name}
        openDeactivateAccountModal={deactivating}
        setOpenDeactivateAccountModal={setDeactivating}
        handleUpdateAccount={() => void refetchCompany()}
      />
      <PlatformAddNewUserDrawer
        open={addingUser}
        handleCloseDrawer={() => setAddingUser(false)}
        handleCreatedUser={() => setRefreshUsers((count) => count + 1)}
        selectedCompany={{ id: accountId, businessLegalName: name }}
      />
      <PlatformUserProfileDrawer
        selectedUserId={profile?.userId}
        open={!!profile}
        edit={profile?.edit ?? false}
        isReactivateUser={profile?.reactivate ?? false}
        handleCloseDrawer={() => setProfile(null)}
        handleUpdateUser={() => setRefreshUsers((count) => count + 1)}
      />
    </Stack>
  );
};

const AccountHolder: React.FC = () => {
  const { accountId = "" } = useParams<{ accountId: string }>();
  return <AccountHolderContent key={accountId} accountId={accountId} />;
};

export default AccountHolder;
